import MuiCheckbox from "@mui/material/Checkbox";
import type { CheckboxProps as MuiCheckboxProps } from "@mui/material/Checkbox";
import { styled } from "@mui/material/styles";
import type { CSSProperties, FC } from "react";
import Box from "@mui/material/Box";

const sizeStyles: Record<NonNullable<CheckboxProps["size"]>, CSSProperties> = {
  large: { width: 28, height: 28 },
  medium: { width: 24, height: 24 },
  small: { width: 16, height: 16 },
};

type IconProps = { size?: CheckboxProps["size"]; isReadonly?: boolean; isDecorative?: boolean };

const Icon = styled("span")<IconProps>(({ size = "medium", theme, isReadonly, isDecorative }) => {
  const { grey, common, primary } = theme.palette;
  return {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "5px",
    border: `1px solid ${grey[700]}`,
    backgroundColor: common.white,
    ...sizeStyles[size],
    ...(isReadonly
      ? {}
      : { "input:disabled ~ &": { backgroundColor: common.white, border: `1px solid ${grey[400]}` } }),
    ...(isDecorative
      ? {}
      : { ".Mui-focusVisible &": { outline: `2px auto ${primary.main}`, outlineOffset: 2, border: "none" } }),
  };
});

const CheckedIcon: FC<IconProps> = ({ size = "medium", isReadonly, isDecorative }) => {
  return (
    <Icon
      isReadonly={isReadonly}
      isDecorative={isDecorative}
      sx={theme => ({
        backgroundColor: `${theme.palette.primary.main}`,
        border: "none",
        ...(isReadonly
          ? {}
          : { "input:disabled ~ &": { backgroundColor: theme.palette.primary["200"], border: "none" } }),
      })}
      size={size}
    >
      <Box component="svg" viewBox="0 0 24 24">
        <path
          fill="white"
          fillRule="evenodd"
          d="M9.889 17.786l-.05-.001a1.549 1.549 0 01-1.05-.46l-3.111-3.142a1.583 1.583 0 010-2.223 1.544 1.544 0 012.053-.13l.147.13 2.01 2.031 8.234-8.316a1.544 1.544 0 012.2 0c.56.566.604 1.457.13 2.074l-.13.148-9.333 9.428a1.56 1.56 0 01-.939.452 1.548 1.548 0 01-.143.009H9.89z"
        />
      </Box>
    </Icon>
  );
};

type CheckboxProps = Omit<
  MuiCheckboxProps,
  | "icon"
  | "disableRipple"
  | "checkedIcon"
  | "color"
  | "action"
  | "centerRipple"
  | "disableTouchRipple"
  | "focusRipple"
  | "focusVisibleClassName"
  | "LinkComponent"
  | "TouchRippleProps"
  | "touchRippleRef"
> & { isReadonly?: boolean };

export const Checkbox: FC<CheckboxProps> = ({ size = "medium", isReadonly, ...rest }) => {
  return (
    <MuiCheckbox
      icon={<Icon size={size} isReadonly={isReadonly} />}
      checkedIcon={<CheckedIcon size={size} isReadonly={isReadonly} />}
      disableRipple
      {...rest}
    ></MuiCheckbox>
  );
};

export const CheckboxMark: FC<{ checked: boolean; size?: CheckboxProps["size"] }> = ({ checked, size = "medium" }) => (
  <Box component="span" aria-hidden="true" sx={{ display: "inline-flex", flexShrink: 0 }}>
    {checked ? <CheckedIcon size={size} isDecorative /> : <Icon size={size} isDecorative />}
  </Box>
);
